import { supabase } from "./supabase";
import { erEkteProduksjon } from "./session";
import type { Member } from "./types";

/**
 * Fagfunksjonen og teamet, fra telefonen.
 *
 * ========  HVORFOR DETTE MAA FINNES I APPEN OGSAA  ========
 *
 * Fotografen bruker appen, ikke nettflata. Lot vi valget bli staaende bare
 * paa nettet, kunne hen aldri bli `tekniker` — og dermed aldri jamme
 * tidskoden, som er nettopp det teknikeren skal gjoere. Et valg som bare
 * finnes der personen ikke er, er ikke et valg.
 *
 * Brukeren 27.10.2026: «Husk at det ikke bare er regi som bruker appen.
 * Alle skal kunne bruke appen for aa lese dokumenter, sjekklister osv.
 * Teknikere skal kunne jamme tidskoden fra appen.»
 *
 * ========  KREVER NETT, OG DET ER GREIT  ========
 *
 * Dette er oppsett, ikke logging. Appen skal kunne LOGGE i en kjeller —
 * den skal ikke kunne bytte rolle der. Uten nett returnerer alt tomt, og
 * menyen sier det i stedet for aa vise en tom liste som ser oedelagt ut.
 *
 * ========  ROLLEN SKRIVES ALDRI HERFRA  ========
 *
 * Bare `craft_id`. Basen utleder rollen, og holder de tre med noekler paa
 * `crew` til noen bekrefter. Se migrasjon 20261027141000.
 */

export interface Fagfunksjon {
  readonly id: string;
  readonly navn: string;
  readonly gruppe: string;
  readonly rolle: Member["role"];
}

export interface Team {
  readonly id: string;
  readonly navn: string;
}

export interface MittValg {
  readonly craftId: string | null;
  readonly unitId: string | null;
  readonly rolle: Member["role"];
  readonly venter: boolean;
}

/** Rollene som maa bekreftes. Speiler `tilgang.krever_godkjenning`. */
const MED_NOEKLER: readonly Member["role"][] = ["prodleder", "produsent", "redaktor"];

export function kreverGodkjenning(rolle: Member["role"]): boolean {
  return MED_NOEKLER.includes(rolle);
}

export async function hentFagfunksjoner(): Promise<readonly Fagfunksjon[]> {
  if (supabase === null || !navigator.onLine) return [];
  const { data, error } = await supabase
    .from("craft")
    .select("id, name, group_name, role")
    .order("sort_order", { ascending: true });
  if (error !== null || data === null) return [];
  return (data as unknown as Record<string, unknown>[]).map((r) => ({
    id: String(r["id"]),
    navn: String(r["name"]),
    gruppe: String(r["group_name"]),
    rolle: String(r["role"]) as Member["role"],
  }));
}

export async function hentTeam(produksjonId: string): Promise<readonly Team[]> {
  if (supabase === null || !erEkteProduksjon(produksjonId) || !navigator.onLine) return [];
  const { data, error } = await supabase
    .from("unit")
    .select("id, name")
    .eq("production_id", produksjonId)
    .order("name", { ascending: true });
  if (error !== null || data === null) return [];
  return (data as unknown as Record<string, unknown>[]).map((r) => ({
    id: String(r["id"]), navn: String(r["name"]),
  }));
}

export async function hentMitt(medlemId: string): Promise<MittValg | null> {
  if (supabase === null || !navigator.onLine) return null;
  const { data, error } = await supabase
    .from("production_member")
    .select("craft_id, unit_id, role, approved_at, craft:craft_id(role)")
    .eq("id", medlemId)
    .maybeSingle();
  if (error !== null || data === null) return null;
  const r = data as unknown as Record<string, unknown>;
  const fag = r["craft"] as { role?: string } | null;
  const oensket = fag?.role as Member["role"] | undefined;
  return {
    craftId: r["craft_id"] == null ? null : String(r["craft_id"]),
    unitId: r["unit_id"] == null ? null : String(r["unit_id"]),
    rolle: String(r["role"]) as Member["role"],
    venter: oensket !== undefined
      && kreverGodkjenning(oensket)
      && r["approved_at"] == null,
  };
}

export async function velgFagfunksjon(
  medlemId: string, craftId: string,
): Promise<boolean> {
  if (supabase === null) return false;
  const { error } = await supabase
    .from("production_member")
    .update({ craft_id: craftId })
    .eq("id", medlemId);
  return error === null;
}

export async function velgTeam(medlemId: string, unitId: string | null): Promise<boolean> {
  if (supabase === null) return false;
  const { error } = await supabase
    .from("production_member")
    .update({ unit_id: unitId })
    .eq("id", medlemId);
  return error === null;
}
